import {useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import {InputField} from '@/components/common';

export type AutoSelectSampleMode = 'coverage' | 'count';

export type AutoSelectSampleParams = {
    mode: AutoSelectSampleMode;
    coveragePercent: number;
    itemCount: number;
};

export type AutoSelectSampleDialogProps = {
    open: boolean;
    populationCount: number;
    suggestedCount?: number | null;
    defaultCoveragePercent?: number;
    onClose: () => void;
    onConfirm: (params: AutoSelectSampleParams) => void;
};

export default function AutoSelectSampleDialog({
    open,
    populationCount,
    suggestedCount,
    defaultCoveragePercent = 10,
    onClose,
    onConfirm,
}: AutoSelectSampleDialogProps) {
    const [mode, setMode] = useState<AutoSelectSampleMode>('coverage');
    const [coveragePercent, setCoveragePercent] = useState(String(defaultCoveragePercent));
    const [itemCount, setItemCount] = useState('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setMode('coverage');
        setCoveragePercent(String(defaultCoveragePercent));
        setItemCount(suggestedCount != null && suggestedCount > 0 ? String(Math.round(suggestedCount)) : '');
        setError(null);
    }, [open, defaultCoveragePercent, suggestedCount]);

    const parsedCoverage = useMemo(() => Number(coveragePercent), [coveragePercent]);
    const parsedCount = useMemo(() => Number(itemCount), [itemCount]);

    const handleConfirm = () => {
        if (mode === 'coverage') {
            if (!Number.isFinite(parsedCoverage) || parsedCoverage <= 0 || parsedCoverage > 100) {
                setError('Enter a coverage percentage between 0.1 and 100.');
                return;
            }
            onConfirm({
                mode: 'coverage',
                coveragePercent: parsedCoverage,
                itemCount: 0,
            });
            return;
        }

        if (!Number.isFinite(parsedCount) || parsedCount <= 0 || !Number.isInteger(parsedCount)) {
            setError('Enter a whole number of items to select.');
            return;
        }
        if (parsedCount > populationCount) {
            setError(`Cannot select more than ${populationCount} items from the population.`);
            return;
        }
        onConfirm({
            mode: 'count',
            coveragePercent: 0,
            itemCount: parsedCount,
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Auto-select sample</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    GL population: {populationCount} invoice{populationCount === 1 ? '' : 's'}.
                    Items with |amount| ≥ performance materiality are always included.
                </Typography>

                <RadioGroup
                    value={mode}
                    onChange={(e) => {
                        setMode(e.target.value as AutoSelectSampleMode);
                        setError(null);
                    }}
                >
                    <FormControlLabel
                        value="coverage"
                        control={<Radio size="small"/>}
                        label="By monetary coverage (%)"
                    />
                    {mode === 'coverage' && (
                        <Box sx={{pl: 4, pb: 1.5}}>
                            <InputField
                                variant="outlined"
                                size="small"
                                fullWidth
                                label="Coverage %"
                                value={coveragePercent}
                                onChange={(e) => {
                                    setCoveragePercent(e.target.value);
                                    setError(null);
                                }}
                                helperText="Round-robin by month until selected amount reaches this % of population."
                            />
                        </Box>
                    )}

                    <FormControlLabel
                        value="count"
                        control={<Radio size="small"/>}
                        label="By number of items"
                    />
                    {mode === 'count' && (
                        <Box sx={{pl: 4}}>
                            <InputField
                                variant="outlined"
                                size="small"
                                fullWidth
                                label="Number of items"
                                value={itemCount}
                                onChange={(e) => {
                                    setItemCount(e.target.value);
                                    setError(null);
                                }}
                                helperText={
                                    suggestedCount != null && suggestedCount > 0
                                        ? `APM suggested total sample size: ${Math.round(suggestedCount)}`
                                        : 'Round-robin by month until this many items are selected.'
                                }
                            />
                        </Box>
                    )}
                </RadioGroup>

                {error && (
                    <Typography variant="caption" color="error" sx={{display: 'block', mt: 1}}>
                        {error}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleConfirm}>
                    Select
                </Button>
            </DialogActions>
        </Dialog>
    );
}
